import { computed, ref } from 'vue'
import { playReminderSound } from '@/utils/reminderSound'

const MAX_VISIBLE = 3
const toastQueue = ref([])
let soundTimer = null

const visibleToasts = computed(() => toastQueue.value.slice(0, MAX_VISIBLE))
const overflowCount = computed(() => Math.max(0, toastQueue.value.length - MAX_VISIBLE))

function parseToastTime(value) {
  if (!value) return 0
  if (value instanceof Date) {
    const timestamp = value.getTime()
    return Number.isNaN(timestamp) ? 0 : timestamp
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0

  const raw = String(value).trim()
  if (!raw) return 0
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T')
  const timestamp = new Date(normalized).getTime()
  return Number.isNaN(timestamp) ? 0 : timestamp
}

function padDatePart(value) {
  return String(value).padStart(2, '0')
}

function formatToastTime(timestamp) {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return ''

  const year = date.getFullYear()
  const month = padDatePart(date.getMonth() + 1)
  const day = padDatePart(date.getDate())
  const hour = padDatePart(date.getHours())
  const minute = padDatePart(date.getMinutes())
  const second = padDatePart(date.getSeconds())
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`
}

function sortToastQueue() {
  toastQueue.value.sort((a, b) => {
    if (b.sortTimestamp !== a.sortTimestamp) {
      return b.sortTimestamp - a.sortTimestamp
    }
    return b.createdAt - a.createdAt
  })
}

function normalizeToastItem(item) {
  const sortTimestamp = parseToastTime(item.sortTime || item.time || item.createTime)
  return {
    content: '',
    meta: '',
    priority: 0,
    actions: [],
    payload: {},
    ...item,
    sortTimestamp,
    time: formatToastTime(sortTimestamp) || item.time || '',
    createdAt: Date.now()
  }
}

function scheduleToastSound() {
  if (soundTimer) return
  soundTimer = setTimeout(() => {
    soundTimer = null
    playReminderSound()
  }, 0)
}

function pushToast(item) {
  if (!item?.id || !item.source || item.rawId === undefined || item.rawId === null) return
  const normalized = normalizeToastItem(item)
  const index = toastQueue.value.findIndex(toast => toast.id === normalized.id)
  if (index > -1) {
    toastQueue.value.splice(index, 1, {
      ...toastQueue.value[index],
      ...normalized,
      createdAt: toastQueue.value[index].createdAt
    })
    sortToastQueue()
    return
  }
  toastQueue.value.push(normalized)
  sortToastQueue()
  scheduleToastSound()
}

function dismissToast(id) {
  toastQueue.value = toastQueue.value.filter(item => item.id !== id)
}

function removeToastBySourceId(source, rawId) {
  toastQueue.value = toastQueue.value.filter(item => !(item.source === source && String(item.rawId) === String(rawId)))
}

function removeToastsBySource(source) {
  toastQueue.value = toastQueue.value.filter(item => item.source !== source)
}

function resetToastCenter() {
  toastQueue.value = []
  if (soundTimer) {
    clearTimeout(soundTimer)
    soundTimer = null
  }
}

export function useGlobalToastCenter() {
  return {
    toastQueue,
    visibleToasts,
    overflowCount,
    pushToast,
    dismissToast,
    removeToastBySourceId,
    removeToastsBySource,
    resetToastCenter
  }
}
